if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        // Gallery navigation arrows. Both the overlay arrows on the main image
        // and the arrows flanking the thumbnail rail carry data-direction and
        // advance the active image. The thumbnail rail follows the image via
        // setActiveThumbnail, which only scrolls the strip once the active
        // thumbnail has moved out of view (i.e. past the last visible one).
        this.navButtons = Array.from(this.querySelectorAll('button[data-direction]'));
        this.prevButtons = this.navButtons.filter((button) => button.dataset.direction === 'previous');
        this.nextButtons = this.navButtons.filter((button) => button.dataset.direction === 'next');
        this.navButtons.forEach((button) => button.addEventListener('click', this.onMainNavClick.bind(this)));
        if (this.navButtons.length) this.updateMainNavState();

        // Listen for slider changes to update our navigation
        if (this.elements.viewer.slider) {
          this.elements.viewer.addEventListener('slideChanged', this.onSliderChanged.bind(this));
        }

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.updateMainNavState();
      }

      onSliderChanged(event) {
        // Handle slider component's slide changes
        if (event.detail && event.detail.currentElement) {
          const mediaId = event.detail.currentElement.dataset.mediaId;
          if (mediaId) {
            this.setActiveMedia(mediaId, false);
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        
        if (!activeMedia) {
          // Fallback to first available media
          const fallbackMedia = this.elements.viewer.querySelector('[data-media-id]');
          if (!fallbackMedia) {
            return;
          }
          return this.setActiveMedia(fallbackMedia.dataset.mediaId, prepend);
        }
        
        // Remove active class from all items
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        
        // Add active class to the target item
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        this.updateMainNavState();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const slider = this.elements.thumbnails.slider;
        if (!slider) return;

        // Desktop shows a vertical thumbnail rail (see the side-rail layout in
        // section-main-product.css); mobile/tablet keep the horizontal strip.
        // Scroll the rail along its actual axis, and only when the active thumb
        // is outside the visible range, so the rail follows the image rather
        // than moving on its own (scrolls to the next batch at the edge).
        if (getComputedStyle(slider).flexDirection === 'column') {
          const sliderRect = slider.getBoundingClientRect();
          const thumbRect = thumbnail.getBoundingClientRect();
          const relTop = thumbRect.top - sliderRect.top + slider.scrollTop;
          const above = relTop < slider.scrollTop;
          const below = relTop + thumbRect.height > slider.scrollTop + slider.clientHeight;
          if (!above && !below) return;
          slider.scrollTo({ top: above ? relTop : relTop + thumbRect.height - slider.clientHeight });
        } else {
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      getMediaItems() {
        const allItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const uniqueItems = [];
        const seenIds = new Set();
        
        // Filter out duplicates, prefer items with longer IDs (template prefixed ones)
        allItems.forEach(item => {
          const mediaId = item.dataset.mediaId;
          const baseId = mediaId.replace(/^.*__main-/, ''); // Extract just the number part
          
          if (!seenIds.has(baseId)) {
            seenIds.add(baseId);
            uniqueItems.push(item);
          } else {
            // If we already have this base ID, prefer the one with template prefix
            const existingIndex = uniqueItems.findIndex(existing => 
              existing.dataset.mediaId.replace(/^.*__main-/, '') === baseId
            );
            if (existingIndex !== -1 && mediaId.includes('template--')) {
              uniqueItems[existingIndex] = item; // Replace with template-prefixed version
            }
          }
        });
        

        return uniqueItems;
      }

      getActiveIndex(mediaItems) {
        const items = mediaItems || this.getMediaItems();
        const activeItem = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (!activeItem) {
          return 0;
        }
        const index = items.indexOf(activeItem);
        if (index === -1) {
          return 0;
        }
        return index;
      }

      onMainNavClick(event) {
        event.preventDefault();
        
        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;
        
        const currentIndex = this.getActiveIndex(mediaItems);
        const isNext = event.currentTarget.dataset.direction === 'next';
        const nextIndex = isNext ? currentIndex + 1 : currentIndex - 1;
        
        if (nextIndex < 0 || nextIndex >= mediaItems.length) return;
        
        const nextItem = mediaItems[nextIndex];
        const nextMediaId = nextItem.dataset.mediaId;
        
        this.setActiveMedia(nextMediaId, false);
      }

      updateMainNavState() {
        if (!this.navButtons || !this.navButtons.length) return;
        const mediaItems = this.getMediaItems();
        const currentIndex = this.getActiveIndex(mediaItems);
        const atStart = currentIndex <= 0;
        const atEnd = currentIndex >= mediaItems.length - 1;
        this.prevButtons.forEach((button) => button.toggleAttribute('disabled', atStart));
        this.nextButtons.forEach((button) => button.toggleAttribute('disabled', atEnd));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
